<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher, onDestroy, tick } from 'svelte';
	import { IconButton, TokenV2 } from '@Shelly/shelly-design-system';
	import { formatTimeLabel } from 'src/helpers/timer_helpers';
	import {
		relayQuickTimerOptions,
		type RelayCloseChrome,
		type RelayQuickTimerOption,
	} from 'src/helpers/relay_priority_layout';

	export let picking = false;
	export let armed = false;
	export let closeChrome: RelayCloseChrome = 'label';
	export let disabled = false;
	export let idPrefix: string;

	const dispatch = createEventDispatcher<{
		select: RelayQuickTimerOption;
		open: void;
		close: void;
	}>();

	const FADE_LEFT_PX = 32;
	const FADE_RIGHT_PX = 12;
	const COLLAPSE_MS = 280;
	const options = relayQuickTimerOptions();

	let scrollEl: HTMLElement | undefined;
	let overlayWidth = 32;
	let showLeftFade = false;
	let collapsing = false;
	let dissolvingId: string | undefined;
	let collapseTimer: ReturnType<typeof setTimeout> | undefined;

	$: overlayAria = picking
		? $format('button_close', { default: 'Close' })
		: armed
			? $format('label_timer_active', { default: 'Timer active' })
			: $format('label_set_timer', { default: 'Set timer' });

	const onScroll = () => {
		showLeftFade = (scrollEl?.scrollLeft ?? 0) > 2;
	};

	const clearCollapseTimer = () => {
		if (collapseTimer) {
			clearTimeout(collapseTimer);
			collapseTimer = undefined;
		}
	};

	const finishCollapse = (then?: () => void) => {
		clearCollapseTimer();
		collapseTimer = setTimeout(() => {
			picking = false;
			collapsing = false;
			dissolvingId = undefined;
			collapseTimer = undefined;
			then?.();
		}, COLLAPSE_MS);
	};

	onDestroy(clearCollapseTimer);

	const open = async () => {
		if (disabled || picking || collapsing) return;
		picking = true;
		collapsing = false;
		dissolvingId = undefined;
		dispatch('open');
		await tick();
		if (scrollEl) scrollEl.scrollLeft = 0;
		onScroll();
	};

	const startCollapse = (then?: () => void) => {
		if (collapsing) return;
		collapsing = true;
		picking = false;
		finishCollapse(then);
	};

	const onOverlayClick = () => {
		if (disabled) return;
		if (picking) {
			startCollapse(() => dispatch('close'));
			return;
		}
		open();
	};

	const onTokenClick = (option: RelayQuickTimerOption) => {
		if (disabled || collapsing || !picking) return;
		dissolvingId = option.id;
		startCollapse(() => dispatch('select', option));
	};
</script>

<div
	class="quick-timer-rail"
	class:is-picking={picking}
	class:is-collapsing={collapsing}
	class:is-armed={armed}
	class:show-left-fade={showLeftFade}
	style="--quick-timer-mask-actions-width: {overlayWidth ||
		32}px; --quick-timer-mask-fade-left: {showLeftFade
		? FADE_LEFT_PX
		: 0}px; --quick-timer-mask-fade-right: {picking || collapsing ? FADE_RIGHT_PX : 0}px;"
>
	<div
		class="quick-timer-scroll"
		bind:this={scrollEl}
		data-prevent-tab-swipe
		data-prevent-sidepanel-swipe
		data-prevent-drawer-swipe
		on:scroll={onScroll}
		role="group"
		aria-label={$format('label_quick_timer_options', { default: 'Quick timer options' })}
		aria-hidden={!picking && !collapsing}
	>
		<div class="quick-timer-tokens">
			{#each options as option (option.id)}
				<TokenV2
					id={`${idPrefix}-${option.id}`}
					size="m"
					title={formatTimeLabel(option.hr, option.min, option.sec, '00').trim()}
					bgColor="--color-2"
					selected={dissolvingId === option.id}
					disabled={disabled || collapsing}
					icon="far fa-clock"
					class={dissolvingId === option.id ? 'is-dissolving' : ''}
					on:click={() => onTokenClick(option)}
				/>
			{/each}
			<!-- TODO: custom timer chip (AddEditCustomTimerModal / DUI field) at the end of this strip -->
			<div class="quick-timer-end-spacer" aria-hidden="true"></div>
		</div>
	</div>
	<div class="quick-timer-overlay" bind:clientWidth={overlayWidth}>
		{#if picking && closeChrome === 'label'}
			<IconButton
				id={`${idPrefix}-close`}
				variant="secondary"
				shape="circle"
				aria-label={overlayAria}
				{disabled}
				on:click={onOverlayClick}
			>
				{$format('button_close', { default: 'Close' })}
			</IconButton>
		{:else}
			<IconButton
				id={`${idPrefix}-overlay`}
				variant="secondary"
				icon={picking ? 'fas fa-times' : 'far fa-clock'}
				isActive={armed && !picking && !disabled}
				aria-label={overlayAria}
				aria-pressed={armed}
				{disabled}
				on:click={onOverlayClick}
			/>
		{/if}
	</div>
</div>

<style>
	.quick-timer-rail {
		position: absolute;
		z-index: 3;
		top: var(--spacing-s);
		right: var(--spacing-s);
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		align-items: center;
		max-width: calc(100% - var(--spacing-l));
		pointer-events: none;
	}

	.quick-timer-rail.is-picking,
	.quick-timer-rail.is-collapsing {
		left: var(--spacing-s);
		right: var(--spacing-s);
		max-width: none;
	}

	.quick-timer-scroll {
		grid-column: 1 / -1;
		grid-row: 1;
		min-width: 0;
		max-width: 0;
		width: 100%;
		overflow-x: auto;
		opacity: 0;
		justify-self: end;
		pointer-events: none;
		scrollbar-width: none;
		-ms-overflow-style: none;
		-webkit-mask-image: linear-gradient(
			to right,
			transparent 0,
			#000 var(--quick-timer-mask-fade-left),
			#000 calc(100% - var(--quick-timer-mask-actions-width) - var(--quick-timer-mask-fade-right)),
			transparent calc(100% - var(--quick-timer-mask-actions-width))
		);
		mask-image: linear-gradient(
			to right,
			transparent 0,
			#000 var(--quick-timer-mask-fade-left),
			#000 calc(100% - var(--quick-timer-mask-actions-width) - var(--quick-timer-mask-fade-right)),
			transparent calc(100% - var(--quick-timer-mask-actions-width))
		);
		transition:
			max-width 320ms cubic-bezier(0.22, 1, 0.36, 1),
			opacity 220ms ease;
	}

	.quick-timer-scroll::-webkit-scrollbar {
		display: none;
	}

	.quick-timer-rail.is-picking .quick-timer-scroll,
	.quick-timer-rail.is-collapsing .quick-timer-scroll {
		max-width: 100%;
		opacity: 1;
		justify-self: stretch;
		pointer-events: auto;
	}

	.quick-timer-rail.is-collapsing .quick-timer-scroll {
		opacity: 0;
		pointer-events: none;
	}

	.quick-timer-tokens {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
		width: max-content;
	}

	.quick-timer-end-spacer {
		flex-shrink: 0;
		width: calc(var(--quick-timer-mask-actions-width) + var(--quick-timer-mask-fade-right));
		height: 1px;
		pointer-events: none;
	}

	.quick-timer-overlay {
		grid-column: 2;
		grid-row: 1;
		z-index: 2;
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		pointer-events: auto;
	}

	.quick-timer-rail :global(.token-wrapper-v2) {
		transition:
			opacity 220ms ease,
			transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
	}

	.quick-timer-rail.is-collapsing :global(.token-wrapper-v2) {
		opacity: 0;
		transform: translateX(12px) scale(0.97);
	}

	.quick-timer-rail.is-collapsing :global(.token-wrapper-v2.is-dissolving) {
		opacity: 0;
		transform: translateX(8px) scale(0.9);
		transition-duration: 180ms;
	}
</style>
